/**
 * sl-m82f.4.3: Reusable drill-down select used by every feature tab.
 *
 * Each tab passes its own `options` so this component stays completely
 * dimension-agnostic — it renders a labeled custom menu that defers
 * to the parent for state and option building.
 *
 * The "All" entry is injected automatically so callers don't have to
 * duplicate it across tabs.
 */
import { Menu, MenuItem, MenuTrigger } from '@affine/component/ui/menu';
import { DoneIcon } from '@blocksuite/icons/rc';
import type { CSSProperties } from 'react';

export interface DrillDownOption {
  value: string;
  label: string;
}

export interface DrillDownSelectProps {
  label: string;
  /** Aria-label shown on the menu trigger for screen readers. */
  ariaLabel?: string;
  allLabel: string;
  options: DrillDownOption[];
  selected: string | null;
  onSelect: (value: string | null) => void;
  /** Hide the select entirely when there are no options (other than "All"). */
  hideWhenEmpty?: boolean;
  disabled?: boolean;
}

const wrapperStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '12px',
  color: 'var(--streamlain-text-secondary-color)',
};

const labelStyle: CSSProperties = {
  whiteSpace: 'nowrap',
};

const triggerStyle: CSSProperties = {
  minWidth: '140px',
  height: '32px',
  borderRadius: '6px',
  border: '1px solid var(--streamlain-border-color)',
  backgroundColor: 'var(--streamlain-background-primary-color)',
  color: 'var(--streamlain-text-primary-color)',
  fontSize: '12px',
};

const menuContentStyle: CSSProperties = {
  minWidth: '180px',
  maxWidth: '280px',
  maxHeight: '320px',
  overflowY: 'auto',
};

const menuItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  width: '100%',
};

const ALL_VALUE = '__all__';

/**
 * Dimension-agnostic drill-down dropdown. Always offers an "All" entry;
 * `onSelect(null)` is called when the user picks it. Consumers should treat
 * `null` as the "no filter" state.
 */
export const DrillDownSelect = ({
  label,
  ariaLabel,
  allLabel,
  options,
  selected,
  onSelect,
  hideWhenEmpty = false,
  disabled = false,
}: DrillDownSelectProps) => {
  if (hideWhenEmpty && options.length === 0) return null;

  const selectedValue = selected ?? ALL_VALUE;
  const menuOptions = [{ value: ALL_VALUE, label: allLabel }, ...options];
  const selectedLabel =
    menuOptions.find(option => option.value === selectedValue)?.label ??
    allLabel;

  return (
    <div style={wrapperStyle}>
      <span style={labelStyle}>{label}</span>
      <Menu
        items={
          <>
            {menuOptions.map(option => {
              const isSelected = option.value === selectedValue;
              return (
                <MenuItem
                  key={option.value}
                  onSelect={() =>
                    onSelect(option.value === ALL_VALUE ? null : option.value)
                  }
                >
                  <div style={menuItemStyle}>
                    <span>{option.label}</span>
                    {isSelected && <DoneIcon fontSize={16} />}
                  </div>
                </MenuItem>
              );
            })}
          </>
        }
        contentOptions={{
          align: 'start',
          style: menuContentStyle,
        }}
      >
        <MenuTrigger
          aria-label={ariaLabel ?? label}
          disabled={disabled}
          style={triggerStyle}
        >
          {selectedLabel}
        </MenuTrigger>
      </Menu>
    </div>
  );
};
